import React from 'react';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle, FiClock, FiCircle } from 'react-icons/fi';
import { SapSyncStatus } from '../../types';

interface SapSyncBadgeProps {
  status: SapSyncStatus | number;
  /** When true, renders a smaller variant fit for table rows. Default: true. */
  compact?: boolean;
}

/**
 * Tri-state pill describing where an Amazon PO sits in the SAP sync flow:
 *   0 = NotSynced -- gray  (default)
 *   1 = Queued    -- orange (user clicked the "Zatwierdź przekazanie do SAP" button)
 *   2 = Synced    -- green  (SAP worker successfully pushed it)
 *
 * Inherits dark/light theming via CSS variables for the muted "not synced" case;
 * the colored states use opaque RGBA backgrounds that work on either theme.
 */
const SapSyncBadge: React.FC<SapSyncBadgeProps> = ({ status, compact = true }) => {
  const { t } = useTranslation();

  const cfg = (() => {
    switch (status) {
      case SapSyncStatus.Synced:
        return {
          color: '#10b981',
          bg: 'rgba(16, 185, 129, 0.12)',
          icon: <FiCheckCircle />,
          label: t('orders.sapSyncSynced'),
        };
      case SapSyncStatus.Queued:
        return {
          color: '#f59e0b',
          bg: 'rgba(245, 158, 11, 0.12)',
          icon: <FiClock />,
          label: t('orders.sapSyncQueued'),
        };
      case SapSyncStatus.NotSynced:
      default:
        return {
          color: 'var(--text-muted)',
          bg: 'rgba(107, 114, 128, 0.1)',
          icon: <FiCircle />,
          label: t('orders.sapSyncNotSynced'),
        };
    }
  })();

  const padding = compact ? '2px 8px' : '4px 12px';
  const fontSize = compact ? '11px' : '12px';
  const iconSize = compact ? '11px' : '13px';

  return (
    <span
      title={cfg.label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        padding,
        borderRadius: '10px',
        fontSize,
        fontWeight: 600,
        color: cfg.color,
        backgroundColor: cfg.bg,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ fontSize: iconSize, display: 'inline-flex' }}>{cfg.icon}</span>
      {cfg.label}
    </span>
  );
};

export default SapSyncBadge;
